//---- components ---------
//---- mixins -------------
import globalMixin from '../common/global-mixin.js';
//---- services -----------
import { ajaxService } from '@/app/services';
import activeTabService from './browser-tab.service.js'
import Notifications from "./notification.js"
//---- other --------------
import { appLocalStorage } from './app-storage.js'
import store from '../store/index.js';
import URL from '../common/URL.js';
//---- 3rd-parties --------
import VueApp from '@/main.js'

const debugTimeout = false

const SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID = "session_about_to_expire"

// const GRACE_PERIOD_MIN = .3; // 20 seconds for debugging
const GRACE_PERIOD_MIN = 5;  // 5 min by default


class AuthService {

  async checkActiveWindowAndTimeout() {
    let currentTimeMillis = (new Date()).getTime()
    let gracePeriod = GRACE_PERIOD_MIN * 60 * 1000; // 5 min by default

    let sessionTimeout = appLocalStorage.getItem('sessionTimeout');
    // make sure the gracePeriod is less than 20% of sessionTimeout
    if (gracePeriod > sessionTimeout * 2 / 10) {
      gracePeriod = sessionTimeout * 2 / 10;
      if (debugTimeout) console.log("grace period: " + gracePeriod)
    }

    // check for timeout
    let timeoutTime = appLocalStorage.getItem('timeout')
    // if there is no timeout time, this means that we're not logged in.
    if (timeoutTime) {
      if (debugTimeout) console.debug("Checking for timeout")
      timeoutTime = parseInt(timeoutTime)
      // if we've already timed out
      // if (currentTimeMillis > timeoutTime) {
      if (currentTimeMillis > timeoutTime) {
        if (debugTimeout) console.debug("Timed out")
        appLocalStorage.removeItem('expiration_notified')
        appLocalStorage.removeItem('timeout')
        Notifications.removeNotification(SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID)
        await store.dispatch('onTimeout')
        globalMixin.methods.safeRouterPush('/login')
      }
      // if we're within the grace period
      else if (currentTimeMillis > timeoutTime - gracePeriod) {
        if (!appLocalStorage.getItem('expiration_notified', false)) {
          appLocalStorage.setItem('expiration_notified', true)
          Notifications.warn("Your session is about to expire", SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID)
          if (debugTimeout) console.debug("Notified of expiration")
        }
        if (debugTimeout) console.debug("In gracePeriod - Timing out in " + ((timeoutTime - currentTimeMillis) / 1000) + " seconds (" + ((timeoutTime - currentTimeMillis) / 60000) + " minutes)")
      }
      else {
        if (debugTimeout) console.debug("In Session - Timing out in " + ((timeoutTime - currentTimeMillis) / 1000) + " seconds (" + ((timeoutTime - currentTimeMillis) / 60000) + " minutes)")
      }
    }
    else {
      // if there's no session, make sure the current tab knows about it
      if (store.getters["auth/loggedIn"]) {
        if (debugTimeout)
          console.debug("logging the user out")
        Notifications.removeNotification(SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID)
        await store.dispatch('onTimeout')
        appLocalStorage.removeItem('timeout')
        globalMixin.methods.safeRouterPush('/login')
      }
    }
    activeTabService.checkActiveWindow(currentTimeMillis)
  }

  setSessionTimeout(sessionTimeout) {
    appLocalStorage.setItem('sessionTimeout', sessionTimeout)
    let timeoutTime = (new Date()).getTime() + sessionTimeout
    if (debugTimeout)
      console.debug("setTimeoutTime: " + timeoutTime)
    appLocalStorage.setItem('timeout', timeoutTime)
  }

  extendSessionTimeout() {
    if (!appLocalStorage.getItem('timeout'))
      return;

    appLocalStorage.removeItem('expiration_notified')
    Notifications.removeNotification(SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID)
    let sessionTimeout = parseInt(new String(appLocalStorage.getItem('sessionTimeout')))
    let timeoutTime = (new Date()).getTime() + sessionTimeout
    if (debugTimeout)
      console.debug("extendTimeoutTime: " + timeoutTime)
    appLocalStorage.setItem('timeout', timeoutTime)
  }

  login(user, type) {

    // just in case it was left out by a previous session
    appLocalStorage.removeItem('expiration_notified')
    Notifications.removeNotification(SESSION_ABOUT_TO_EXPIRE_NOTIFICATION_ID)

    const processResponse = (response) => {
      // not authenticated
      if (response.status === 401) {
        this.logout();
        location.reload(true); // why do we reload the page?

        const errorMessage = response.data && response.data.message;
        return Promise.reject(errorMessage);
      }

      if (response.data?.connectivityStatus) {
        const connectivity = {
            browserConnectivity: navigator.onLine,
            backendInternetConnectivity: response.data.connectivityStatus.internetConnectivity || false,
            hServiceConnectivity: response.data.connectivityStatus.hServiceConnectivity || false,
        }
        store.dispatch("kmOffline/updateConnectedState", connectivity);
      }

      if (response.data.header) {
        // console.log("header/update")
        store.dispatch('header/update', response.data.header);
      }

      return Promise.resolve(response.data)
    }

    // if user is not passed, assume the user info is on the server using the relayState
    if (!user) {
      const url = type == "check" ? URL.checkLoginUrl : URL.loginUrl;
      return ajaxService.post(url)
        .then(response => processResponse(response))
        .catch(error => { 
           if(error.response.data.logFolderReadOnly){
            Notifications.warn(VueApp.$t('label.error.folder-read-only', ['tomcat/logs']));
           }
           if(error.response.data.dataFolderReadOnly){
            Notifications.warn(VueApp.$t('label.error.folder-read-only', ['data']));
           }
           return Promise.reject(error);
        })
    }

    const data = {};

    if (user.token)
      data.token = user.token;
    if (user.force_new_session)
      data.forceNewSession = user.force_new_session;
    if (user.notFromLoginPage)
      data.notFromLoginPage = user.notFromLoginPage;

    return ajaxService.postData(
      URL.loginUrl,
      data
    ).then(response => processResponse(response))
     .catch(error => Promise.reject(error))
  } // end login(user)

  reloadHeader() {
    try {
      return ajaxService.get(URL.reloadHeaderUrl)
    } catch (e) {
      // console.log("Could not reload header. Session has likely timed out.");
    }
  }

  //forget password
  recoverPwd(formData) {
    return ajaxService.post(URL.recoverPwdUrl, formData)
  }
}

export default new AuthService();